import { ChevronRightIcon } from "@/components/icons";
import { Link } from "@tanstack/react-router";
import { cn } from "cn";

import {
  isKnownResource,
  resourceTarget,
  type Resource,
} from "../../app/resources.ts";

/**
 * El enlace a lo que el agente acaba de producir (§9).
 *
 * El backend dice QUÉ es --un lote, una mecánica-- y aquí se decide adónde
 * lleva. Por eso no hay ninguna URL en el backend y mover una pantalla no
 * rompe los enlaces de conversaciones viejas.
 *
 * Se dibuja como una pastilla y no como un botón: no es una acción, es una
 * salida hacia otro sitio, y compite con la confirmación que puede estar al
 * lado.
 */
export function ResourceLink({ resource }: { resource: Resource }) {
  const target = resourceTarget(resource);

  /**
   * Un tipo que esta versión no conoce NO se esconde.
   *
   * Es lo que permite que el backend empiece a mandar tipos nuevos antes de
   * que existan aquí. No dibujar nada se lee igual que un fallo --el usuario
   * ve que el agente dice "ya lo tienes ahí" y no hay nada-- así que se
   * enseña apagado y diciendo por qué.
   */
  if (target === null) {
    return (
      <p className="text-muted-foreground text-xs">
        {resource.label} —{" "}
        {isKnownResource(resource)
          ? "no tiene pantalla propia todavía"
          : "esta versión de la aplicación todavía no sabe abrir esto"}
      </p>
    );
  }

  return (
    <Link
      // El resolutor devuelve rutas de este mismo router; el tipado de
      // TanStack no puede seguirlas a través de la tabla, y comprobarlo es
      // justo lo que hace `resources.test.ts`.
      to={target.to as never}
      params={target.params as never}
      search={
        ((current: Record<string, unknown>) => ({
          ...current,
          ...target.search,
        })) as never
      }
      className={cn(
        "inline-flex w-fit items-center gap-1 rounded-full border px-2.5 py-1",
        "text-xs font-medium transition-colors",
        "hover:border-primary hover:text-primary",
      )}
    >
      {resource.label}
      <ChevronRightIcon className="size-3.5" />
    </Link>
  );
}
